import * as React from 'react';
import { css } from 'styled-components';

// Module Context
import { useComponentContext } from 'src/components/common/modules/component-layout/component-context';

// Components
import Layout from 'src/components/common/theme/Layout';
import { FigureComponent, Typo } from 'src/components/common/ui';
import { checkValue, LightenDarkenColor, appendMcicid } from 'src/components/common/helper';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';
import { UitkButtonSize } from 'uitk-react-button';
import VideoDetails from 'src/components/common/modules/video/videoJSPlayer/video-detail';
import { LazyImage } from 'src/components/common/modules/lazy/lazy-image';
import { ModuleColorBarProps } from 'src/components/common/modules/module-color-bar/module-color-bar';
import { getDeviceImage } from 'src/components/common/theme/component';

const placeholder = 'https://mesocp3.azureedge.net/media/c32fmcus/static-img.jpg';

const IntroContentBlock: React.FC = () => {
  const [loadedClass, setLoadedClass] = React.useState('');
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps: { data, key, defaultSetting: { deviceSize, site }, mccidData: { campaignUrl, referrerId, templateType , extraParam="" } },
  } = useComponentContext();

  const {
    introHeadline,
    uniquekey,
    IntroDescription,
    textColor,
    backgroundColor,
    topRightRibbonColor,
    topMiddleRibbonColor,
    topLeftRibbonColor,
    image,
    turnOffGradient,
    imageCaption,
    imageCopyright,  
    imageDescription,
    backgroundPhotoStaticVideoImage,
    youtubeId,
    videoFile,
    playIcon,
    contentHeadline,
    contentDescription,
    cTAText,
    cTAUrl,
    cTABackgroundColor,
    cTATextColor,
    openUrlInNewTab,
    cTAStyle,
  } = data

  // Dynamic style overrides
  const moduleCss = () => {
    const darkenColor = checkValue(data?.cTABackgroundColor) ? LightenDarkenColor(data?.cTABackgroundColor, -0.1) : '';
    return `
      ${checkValue(backgroundColor) ? `.intro-content-block-wrapper .top-container {
        background-color: ${backgroundColor}
      }` : '' }

      ${checkValue(textColor) ? 
        `
        .intro-content-block-wrapper .top-container .content-container-upper h2 {
          color: ${textColor} !important;
        }
        .intro-content-block-wrapper .top-container .content-container-upper p {
          color: ${textColor} !important;
        }
        `
        : '' }
      
      ${checkValue(cTABackgroundColor) && cTAStyle !== "Secondary" ? 
          `
          .intro-content-block-wrapper .button-container button {
            background-color: ${cTABackgroundColor} !important;
          }
          .intro-content-block-wrapper .button-container button:hover {
            background-color: ${darkenColor} !important;  
          }
          `
          : '' }
      
      ${checkValue(cTATextColor) ? 
        `
        .intro-content-block-wrapper .button-container button {
          color: ${cTATextColor} !important;
        }
        `
        : '' }
    `;
  };

  const colorBars: ModuleColorBarProps = {
    leftRibbonColor: topLeftRibbonColor,
    middleRibbonColor: topMiddleRibbonColor,
    rightRibbonColor: topRightRibbonColor
  }

  const imageComponent = () => {
    const img = checkValue(image, true) && getDeviceImage(image, deviceSize);
    return (
      <FigureComponent
        src={checkValue(img) ? img : placeholder}
        alt={imageDescription}
        exClass='intro-content-block-main-image'
        isGradientActive={turnOffGradient.toLowerCase() !== 'true'}
        colorBarProps={{...colorBars}}
        copyRightProps={{
          copyrightText: imageCopyright,
          copyrightCation: '',
          position: 'br',
          iconSize: 'md'
        }}
      />
    )
  }

  const ctaProps: PrimaryButtonProps = {
    text: cTAText,
    tag: 'button',
    gaTracking: {
      eventCategory: 'Button Click',
      eventAction: 'Intro content block',
      eventLabel: cTAText,
    },
    url: checkValue(cTAUrl) ? appendMcicid(cTAUrl, templateType, campaignUrl, '', referrerId , extraParam) : '',
    target: openUrlInNewTab === 'True' ? '_blank' : '_self',
    size: UitkButtonSize.LARGE,
    exClass: checkValue(cTAStyle) ? `intro-content-btn-${cTAStyle.toLowerCase()}` : ""
  }

  const videoProps = () => {
    const videoProps = {
      key: uniquekey,
      videoFile: videoFile?.[0]?.url ? [videoFile?.[0]] : false,
      youtubeId: youtubeId,
      staticImage: backgroundPhotoStaticVideoImage?.[0]?.url ? backgroundPhotoStaticVideoImage : false,
      copyrightText: '',
      device: deviceSize,
      siteid: site?.id,
      campaignUrl: site?.campaignUrl,
      template: site?.templateName,
      autoplay: false,
      muted: false,
      loadedClass: 'image-loaded',
      colorBarProps: {...colorBars},
      playIcon: playIcon
    };
    return videoProps;
  };

  const updateLoaded = (isLoaded) => {
    if (isLoaded) {
      setLoadedClass('image-loaded');
    } else {
      setLoadedClass('');
    }
  };

  const isVideo = checkValue(videoFile?.[0]?.url) || checkValue(youtubeId);
  const isTopContainerBlank = !checkValue(introHeadline) && !checkValue(IntroDescription);
  const isBottomContentAvailable = [contentHeadline, contentDescription, cTAText, cTAUrl].some(el => checkValue(el));

  return (
    <Layout
      css={css`
        ${moduleCss()}
      `}
      className="intro-content-block"
      HtmlTag="section"
      id={key?.next()}      
    >
      <div className={`intro-content-block-wrapper ${isTopContainerBlank && !checkValue(backgroundColor) ? 'remove-top-container' : ''} ${checkValue(backgroundColor) ? "remove-top-padding" : ""}`}>
        <div className={`top-container ${isTopContainerBlank && checkValue(backgroundColor) ? 'blank-top-container' : ''}`}>
          <div className='container'>
            <div className='content-container-upper'>
              { checkValue(introHeadline) ? <Typo.HTMLText exClass='heading' tag='h2'>{introHeadline}</Typo.HTMLText> : null }
              { checkValue(IntroDescription) ? <Typo.HTMLText tag='p'>{IntroDescription}</Typo.HTMLText> : null }
            </div>
          </div>
        </div>
        <div className='bottom-container'>
          <div className='container'>
            <div className={`content-container-middle  ${imageCaption ? 'with-image-caption' : ''} ${!isBottomContentAvailable ? 'without-bottom-container' : ''}`}>
              <div className={`image-wrapper ${loadedClass}`}>
                <LazyImage
                  updateLoaded={updateLoaded}
                  isGallery={false}
                  device={deviceSize}
                >
                  { isVideo ? <VideoDetails {...videoProps()} /> : imageComponent() }
                  { imageCaption ?  <Typo.HTMLText tag='p' exClass='intro-contnet-block-image-caption'>{imageCaption}</Typo.HTMLText> : null }
                </LazyImage>
              </div>
            </div>
            { isBottomContentAvailable ? 
            <div className={`content-container-lower ${!(checkValue(cTAText) && checkValue(cTAUrl)) ? 'without-cta' : ''}`}>
              { checkValue(contentHeadline) ? <Typo.HTMLText exClass='subheading' tag='h3'>{contentHeadline}</Typo.HTMLText> : null }
              { checkValue(contentDescription) ? <Typo.HTMLText tag='p'>{contentDescription}</Typo.HTMLText> : null }
              { checkValue(cTAText) && checkValue(cTAUrl) ? 
                <div className='button-container'>
                  <PrimaryButton {...ctaProps} />
                </div>
              : null }
            </div>
            : null }
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IntroContentBlock;
